import { useState, useEffect, useCallback } from 'react';
import { getJobCommissionBreakdown } from '../api/jobs-api';
import type { JobCommissionBreakdownDto } from '../types';

interface UseJobCommissionBreakdownReturn {
  data: JobCommissionBreakdownDto | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useJobCommissionBreakdown(
  jobId: number,
  enabled = true
): UseJobCommissionBreakdownReturn {
  const [data, setData] = useState<JobCommissionBreakdownDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCommissionBreakdown = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);
    try {
      const breakdown = await getJobCommissionBreakdown(jobId);
      setData(breakdown);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch commission breakdown');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, enabled]);

  useEffect(() => {
    fetchCommissionBreakdown();
  }, [fetchCommissionBreakdown]);

  return { data, isLoading, error, refetch: fetchCommissionBreakdown };
}
